import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import ModalLayout from '../../../components/structure/ModalLayout';
import FooterBar from '../../../components/structure/FooterBar';
import ApplicationVariable from '../../../models/applications/ApplicationVariable';
import TextField from '../../../components/forms/TextField';
import TextAreaField from '../../../components/forms/TextAreaField';
import Button from '../../../components/general/Button';
import { color_shades_dark, color_shades_darker, color_shades_darkest, color_colors_ocean } from '../../../constants/colors';

const closeIcon = require('../../../content/images/close_icon.svg');
const deleteIcon = require('../../../content/images/content-item-icons/delete-blue.svg');

interface ApplicationVariableEditModalProps {
    variable: ApplicationVariable;
    isVisible: boolean;
    onClose: () => void;
    onSave: (variable: ApplicationVariable) => void;
    onDelete: (variable: ApplicationVariable) => void;
}

const ApplicationVariableEditModal: React.FC<ApplicationVariableEditModalProps> = (props) => {
    const [name, setName] = useState(props.variable.name || '');
    const [value, setValue] = useState(props.variable.value || '');
    const [nameError, setNameError] = useState('');
    const [valueError, setValueError] = useState('');

    useEffect(() => {
        setName(props.variable.name || '');
        setValue(props.variable.value || '');
        setNameError('');
        setValueError('');
    }, [props.variable]);

    const validateForm = () => {
        let isValid = true;
        
        if (!name.trim()) {
            setNameError('Constant name is required');
            isValid = false;
        } else {
            setNameError('');
        }

        if (!value.trim()) {
            setValueError('Constant value is required');
            isValid = false;
        } else {
            setValueError('');
        }

        return isValid;
    };

    const handleSave = () => {
        if (validateForm()) {
            const updatedVariable: ApplicationVariable = {
                ...props.variable,
                name: name.trim(),
                value: value.trim()
            };
            props.onSave(updatedVariable);
        }
    };

    const handleDelete = () => {
        props.onDelete(props.variable);
    };

    const isNewVariable = !props.variable.id;

    return (
        <ModalLayout
            className={modalStyle}
            isVisible={props.isVisible}
            onClose={props.onClose}
            zIndex={1010}
        >
            <div className={containerStyle}>
                <div className="header-wrapper">
                    <h2>{isNewVariable ? 'Create Constant' : 'Edit Constant'}</h2>
                    <Button 
                        className="close-icon" 
                        themes={['end-tight', 'icon']} 
                        icon={closeIcon} 
                        type="button" 
                        onClick={props.onClose} 
                    />
                </div>

                {!isNewVariable && (
                    <div onClick={handleDelete} className="delete-confirmation">
                        <div>Delete Constant</div>
                        <img src={deleteIcon} alt="Delete" />
                    </div>
                )}

                <div className="form-container">
                    <TextField
                        name="name"
                        value={name}
                        placeholder="Enter constant name"
                        label="Constant Name"
                        onChange={(e) => setName(e.target.value)}
                        useForm={false}
                    />
                    {nameError && <div className="error-message">{nameError}</div>}

                    <TextAreaField
                        name="value"
                        value={value}
                        placeholder="Enter constant value"
                        label="Constant Value"
                        onChange={(e) => setValue(e.target.value)}
                        minRows={8}
                        maxRows={25}
                        useForm={false}
                        showResponseVariableTest
                    />
                    {valueError && <div className="error-message">{valueError}</div>}
                </div>

                <FooterBar>
                    <Button
                        themes={['primary', 'wide']}
                        onClick={handleSave}
                        text={isNewVariable ? 'Create' : 'Save'}
                        type="button"
                    />
                    <Button
                        themes={['white', 'end']}
                        onClick={props.onClose}
                        text="Cancel"
                        type="button"
                    />
                </FooterBar>
            </div>
        </ModalLayout>
    );
};

const modalStyle = css`
    width: 66vw;
    height: auto;
    max-height: 80vh;
    min-height: 850px;
    display: flex;
    flex-direction: column;
`;

const containerStyle = css`
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 80vh;
    overflow: hidden;

    .header-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 24px 32px 16px 32px;
        border-bottom: solid 1px ${color_shades_dark};
        flex-shrink: 0;

        h2 {
            margin: 0;
            font-size: 24px;
            font-weight: 600;
            color: ${color_shades_darkest};
        }

        .close-icon {
            cursor: pointer;
        }
    }

    .delete-confirmation {
        color: ${color_colors_ocean};
        position: absolute;
        right: 32px;
        top: 120px;
        cursor: pointer;
        display: flex;
        align-items: center;
        z-index: 1;

        div {
            font-style: normal;
            font-weight: normal;
            font-size: 14px;
            line-height: 20px;
            margin-right: 8px;
        }

        img {
            width: 16px;
            height: 16px;
        }

        &:hover {
            opacity: 0.7;
        }
    }

    .form-container {
        padding: 32px;
        padding-bottom: 16px;
        flex: 1;
        overflow-y: auto;
        min-height: 0;

        .error-message {
            color: #e74c3c;
            font-size: 12px;
            margin-top: 4px;
            margin-bottom: 16px;
        }
    }
`;

export default ApplicationVariableEditModal;
